import React, { useState, useEffect, useMemo, useRef } from "react";
import axios from "axios";
import { toast } from "sonner";
import { Lock, LogOut, Users, MapPin, RefreshCcw, Loader2, Image as ImgIcon, Film, Trash2, Upload, BookOpen, Building2, Eye, UserCheck, Calendar, Clock, HandCoins, Plus, DollarSign, Download } from "lucide-react";
import { PROVINCIAS_AR } from "../lib/content";
import { LogoMark } from "../components/Logo";
import { PressManager } from "../components/admin/PressManager";
import { RedAdmin } from "../components/red/RedAdmin";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;
const STORAGE_KEY = "frenteya_admin_key";

export const Admin = () => {
  const [authed, setAuthed] = useState(false);
  const [key, setKey] = useState("");
  const [loading, setLoading] = useState(false);
  const [rows, setRows] = useState([]);
  const [stats, setStats] = useState(null);
  const [tab, setTab] = useState("supporters"); // "supporters" | "media" | "organizers" | "book"
  const [media, setMedia] = useState([]);
  const [organizers, setOrganizers] = useState([]);
  const [visits, setVisits] = useState(null);
  const [donors, setDonors] = useState([]);
  const [donorStats, setDonorStats] = useState(null);

  useEffect(() => {
    const saved = localStorage.getItem(STORAGE_KEY);
    if (saved) {
      setKey(saved);
      tryAuth(saved);
    }
    // eslint-disable-next-line
  }, []);

  const tryAuth = async (k) => {
    setLoading(true);
    try {
      await axios.post(`${API}/admin/login`, { key: k });
      localStorage.setItem(STORAGE_KEY, k);
      setAuthed(true);
      await Promise.all([loadRows(k), loadStats(k), loadMedia(), loadOrganizers(k), loadVisits(k), loadDonors(k)]);
    } catch (e) {
      localStorage.removeItem(STORAGE_KEY);
      toast.error("Clave incorrecta");
    } finally {
      setLoading(false);
    }
  };

  const loadRows = async (k) => {
    try {
      const { data } = await axios.get(`${API}/admin/supporters`, {
        headers: { "X-Admin-Key": k || key },
      });
      setRows(data);
    } catch (e) { /* ignore */ }
  };

  const loadStats = async (k) => {
    try {
      const { data } = await axios.get(`${API}/admin/stats`, {
        headers: { "X-Admin-Key": k || key },
      });
      setStats(data);
    } catch (e) { /* ignore */ }
  };

  const loadMedia = async () => {
    try {
      const { data } = await axios.get(`${API}/media`);
      setMedia(data);
    } catch (e) { /* ignore */ }
  };

  const loadOrganizers = async (k) => {
    try {
      const { data } = await axios.get(`${API}/admin/organizers`, {
        headers: { "X-Admin-Key": k || key },
      });
      setOrganizers(data);
    } catch (e) { /* ignore */ }
  };

  const loadVisits = async (k) => {
    try {
      const { data } = await axios.get(`${API}/admin/visits`, {
        headers: { "X-Admin-Key": k || key },
      });
      setVisits(data);
    } catch (e) { /* ignore */ }
  };

  const loadDonors = async (k) => {
    const hk = k || key;
    try {
      const [list, stats] = await Promise.all([
        axios.get(`${API}/admin/donors`, { headers: { "X-Admin-Key": hk } }),
        axios.get(`${API}/admin/donors/stats`, { headers: { "X-Admin-Key": hk } }),
      ]);
      setDonors(list.data);
      setDonorStats(stats.data);
    } catch (e) { /* ignore */ }
  };

  const deleteDonor = async (id) => {
    if (!window.confirm("¿Eliminar este aporte?")) return;
    try {
      await axios.delete(`${API}/admin/donors/${id}`, {
        headers: { "X-Admin-Key": key },
      });
      toast.success("Aporte eliminado");
      await loadDonors();
    } catch (e) {
      toast.error("No se pudo eliminar");
    }
  };

  const logout = () => {
    localStorage.removeItem(STORAGE_KEY);
    setAuthed(false);
    setKey("");
    setRows([]);
    setStats(null);
    setMedia([]);
    setOrganizers([]);
    setVisits(null);
    setDonors([]);
    setDonorStats(null);
  };

  const refresh = async () => {
    setLoading(true);
    await Promise.all([loadRows(), loadStats(), loadMedia(), loadOrganizers(), loadVisits(), loadDonors()]);
    setLoading(false);
    toast.success("Actualizado");
  };

  const deleteMedia = async (id) => {
    if (!window.confirm("¿Eliminar este elemento?")) return;
    try {
      await axios.delete(`${API}/admin/media/${id}`, {
        headers: { "X-Admin-Key": key },
      });
      setMedia((m) => m.filter((x) => x.id !== id));
      toast.success("Eliminado");
    } catch (e) {
      toast.error("No se pudo eliminar");
    }
  };

  const [reindexing, setReindexing] = useState(false);
  const [bookStats, setBookStats] = useState(null);
  const [downloads, setDownloads] = useState(null);

  useEffect(() => {
    if (!authed) return;
    axios.get(`${API}/book/stats`).then(({ data }) => setBookStats(data)).catch(() => {});
    axios
      .get(`${API}/admin/book/downloads`, { headers: { "X-Admin-Key": key } })
      .then(({ data }) => setDownloads(data))
      .catch(() => {});
  }, [authed, key]);

  const reindexBook = async () => {
    if (!window.confirm("Esto reindexa el libro completo. Tomará 20-40 segundos. ¿Continuar?")) return;
    setReindexing(true);
    try {
      const { data } = await axios.post(`${API}/admin/book/reindex`, null, {
        headers: { "X-Admin-Key": key },
        timeout: 90000,
      });
      setBookStats({ total_paragraphs: data.total_paragraphs, total_pages: data.total_pages });
      toast.success(`Libro reindexado · ${data.total_paragraphs} párrafos en ${data.total_pages} páginas`);
    } catch (e) {
      toast.error(e.response?.data?.detail || "Error al reindexar");
    } finally {
      setReindexing(false);
    }
  };

  const [seeding, setSeeding] = useState(false);
  const seedContent = async () => {
    if (!window.confirm("Esto carga los flyers, stickers y videos iniciales del movimiento. Idempotente (no duplica). ¿Continuar?")) return;
    setSeeding(true);
    try {
      const { data } = await axios.post(`${API}/admin/seed-content`, null, {
        headers: { "X-Admin-Key": key },
        timeout: 60000,
      });
      toast.success(
        `${data.inserted_images} imágenes/stickers + ${data.inserted_videos} video(s) cargados · ${data.skipped_already_existing} ya existían`
      );
      await loadMedia();
    } catch (e) {
      toast.error(e.response?.data?.detail || "Error al cargar contenido");
    } finally {
      setSeeding(false);
    }
  };

  if (!authed) {
    return (
      <div className="min-h-screen bg-[#0A192F] flex items-center justify-center px-5 relative overflow-hidden" data-testid="admin-login">
        <div aria-hidden className="absolute inset-0 grid-lines opacity-20" />
        <div aria-hidden className="absolute -top-32 -left-32 w-96 h-96 bg-[#2563EB]/20 blur-3xl rounded-full" />
        <div aria-hidden className="absolute -bottom-32 -right-32 w-96 h-96 bg-[#F5B400]/15 blur-3xl rounded-full" />

        <div className="relative w-full max-w-md">
          {/* Silueta de acceso */}
          <div className="flex flex-col items-center mb-8">
            <div className="relative w-28 h-28 rounded-full border-4 border-[#F5B400] bg-white/5 flex items-center justify-center mb-4 shadow-2xl">
              <svg
                viewBox="0 0 64 64"
                className="w-20 h-20 text-white/90"
                fill="currentColor"
                aria-hidden
              >
                {/* Silueta tipo avatar */}
                <circle cx="32" cy="22" r="11" />
                <path d="M12 56c0-11 9-20 20-20s20 9 20 20v4H12v-4z" />
              </svg>
              <div className="absolute -bottom-1 -right-1 w-9 h-9 bg-[#F5B400] rounded-full flex items-center justify-center border-4 border-[#0A192F]">
                <Lock className="text-[#0A192F]" size={14} />
              </div>
            </div>
            <div className="font-headings uppercase tracking-[0.3em] text-[10px] text-[#F5B400]">
              Acceso restringido
            </div>
            <h1 className="font-headings text-3xl font-bold uppercase text-white mt-1">
              Panel Frente Ya
            </h1>
          </div>

          {/* Form */}
          <form
            onSubmit={(e) => {
              e.preventDefault();
              tryAuth(key);
            }}
            className="border border-white/15 bg-white/[0.03] p-7 sm:p-8 space-y-5"
          >
            <div>
              <label className="block text-[10px] uppercase tracking-[0.25em] text-white/60 mb-2 font-headings">
                Clave de administrador
              </label>
              <input
                type="password"
                value={key}
                onChange={(e) => setKey(e.target.value)}
                placeholder="••••••••"
                className="input-line !text-white !border-white/30 focus:!border-[#F5B400]"
                data-testid="admin-key-input"
                autoFocus
              />
            </div>
            <button
              type="submit"
              disabled={loading || !key}
              className="w-full bg-[#F5B400] hover:bg-[#d99f00] disabled:opacity-60 text-[#0A192F] font-headings uppercase tracking-wide py-3.5 inline-flex items-center justify-center gap-2 transition-colors"
              data-testid="admin-login-btn"
            >
              {loading ? <Loader2 className="animate-spin" size={18} /> : <Lock size={16} />}
              {loading ? "Verificando…" : "Ingresar"}
            </button>
          </form>

          <p className="text-center mt-6 text-[10px] uppercase tracking-[0.25em] text-white/40 font-headings">
            Frente Ya · ImPosible Ya↑
          </p>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-[#F3F4F6]" data-testid="admin-dashboard">
      <header className="bg-white border-b border-gray-200">
        <div className="max-w-7xl mx-auto px-5 sm:px-8 lg:px-12 h-16 flex items-center justify-between">
          <LogoMark />
          <div className="flex items-center gap-3">
            <button
              onClick={refresh}
              className="text-sm text-[#0A192F] hover:text-[#0A2463] inline-flex items-center gap-2"
              data-testid="admin-refresh"
            >
              <RefreshCcw size={16} /> Actualizar
            </button>
            <button
              onClick={logout}
              className="text-sm text-[#0A192F] hover:text-red-600 inline-flex items-center gap-2"
              data-testid="admin-logout"
            >
              <LogOut size={16} /> Salir
            </button>
          </div>
        </div>
      </header>

      <main className="max-w-7xl mx-auto px-5 sm:px-8 lg:px-12 py-12">
        <h2 className="font-headings text-3xl sm:text-4xl uppercase font-bold text-[#0A192F] mb-2">
          Panel del movimiento
        </h2>
        <p className="text-[#4B5563] mb-8">Gestioná simpatizantes y contenido multimedia.</p>

        {/* Visitas — siempre visible */}
        {visits && (
          <div className="mb-10" data-testid="admin-visits">
            <div className="flex items-center gap-3 mb-4">
              <Eye size={18} className="text-[#0A2463]" />
              <h3 className="font-headings uppercase tracking-wide text-sm text-[#0A192F]">
                Visitas al sitio
              </h3>
            </div>
            <div className="grid grid-cols-2 lg:grid-cols-5 gap-px bg-gray-200 border border-gray-200">
              <Stat icon={<Eye className="text-[#F5B400]" />} label="Total páginas vistas" value={visits.total} testid="visits-total" />
              <Stat icon={<UserCheck className="text-[#0A2463]" />} label="Visitantes únicos" value={visits.unique_total} testid="visits-unique" />
              <Stat icon={<Clock className="text-[#0A2463]" />} label="Últimas 24 hs" value={visits.last_24h} testid="visits-24h" />
              <Stat icon={<Calendar className="text-[#0A2463]" />} label="Últimos 7 días" value={visits.last_7d} testid="visits-7d" />
              <Stat icon={<UserCheck className="text-[#F5B400]" />} label="Únicos · 7 días" value={visits.unique_7d} testid="visits-unique-7d" />
            </div>

            {visits.daily?.length > 0 && (
              <div className="bg-white border border-gray-200 border-t-0 p-5">
                <div className="text-[10px] uppercase tracking-[0.25em] text-[#4B5563] font-headings mb-3">
                  Últimos 7 días
                </div>
                <DailyChart data={visits.daily} />
              </div>
            )}
          </div>
        )}

        {/* Tabs */}
        <div className="flex gap-2 mb-8 border-b border-gray-300 overflow-x-auto whitespace-nowrap" data-testid="admin-tabs">
          {[
            { id: "supporters", label: "Simpatizantes" },
            { id: "organizers", label: "Organizadores" },
            { id: "donors", label: "Aportantes" },
            { id: "media", label: "Multimedia" },
            { id: "book", label: "Libro" },
            { id: "press", label: "Prensa" },
            { id: "red", label: "Red de contenidos" },
          ].map((t) => (
            <button
              key={t.id}
              onClick={() => setTab(t.id)}
              className={`px-5 py-3 font-headings uppercase tracking-wide text-sm border-b-2 -mb-px transition-colors ${
                tab === t.id
                  ? "border-[#0A2463] text-[#0A2463]"
                  : "border-transparent text-[#9CA3AF] hover:text-[#0A192F]"
              }`}
              data-testid={`admin-tab-${t.id}`}
            >
              {t.label}
            </button>
          ))}
        </div>

        {tab === "supporters" && (
          <>
            {stats && (
              <div className="grid sm:grid-cols-2 lg:grid-cols-6 gap-px bg-gray-200 border border-gray-200 mb-10" data-testid="admin-stats">
                <Stat icon={<Users className="text-[#F5B400]" />} label="Total simpatizantes" value={stats.total} />
                <Stat icon={<Users className="text-[#0A2463]" />} label="Voluntarios" value={stats.voluntarios} />
                <Stat icon={<Building2 className="text-[#0A2463]" />} label="Organizadores" value={stats.organizadores ?? 0} />
                <Stat
                  icon={<HandCoins className="text-[#F5B400]" />}
                  label="Aportantes"
                  value={
                    <span className="flex items-baseline gap-2">
                      <span>{stats.aportantes ?? 0}</span>
                      {stats.monto_total > 0 && (
                        <span className="text-sm text-emerald-600 font-headings tabular-nums">
                          ${Math.round(stats.monto_total).toLocaleString("es-AR")}
                        </span>
                      )}
                    </span>
                  }
                  testid="stat-aportantes"
                />
                <Stat
                  icon={<Download className="text-[#F5B400]" />}
                  label="Descargas del libro"
                  value={stats.descargas_libro ?? 0}
                  testid="stat-descargas-libro"
                />
                <Stat icon={<MapPin className="text-[#0A2463]" />} label="Provincias activas" value={stats.por_provincia.length} />
              </div>
            )}

            {stats?.por_provincia?.length > 0 && (
              <div className="bg-white border border-gray-200 p-6 mb-10">
                <h3 className="font-headings uppercase tracking-wide text-lg text-[#0A192F] mb-4">
                  Top provincias
                </h3>
                <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-3">
                  {stats.por_provincia.slice(0, 9).map((p) => (
                    <div key={p.provincia} className="flex justify-between items-center border-b border-gray-200 py-2">
                      <span className="text-sm text-[#0A192F]">{p.provincia}</span>
                      <span className="font-headings font-bold tabular-nums text-[#0A2463]">{p.total}</span>
                    </div>
                  ))}
                </div>
              </div>
            )}

            <div className="bg-white border border-gray-200" data-testid="admin-supporters-table">
              <div className="px-6 py-4 border-b border-gray-200 flex items-center justify-between">
                <h3 className="font-headings uppercase tracking-wide text-lg text-[#0A192F]">
                  Registros ({rows.length})
                </h3>
              </div>
              <div className="overflow-x-auto">
                <table className="w-full text-sm">
                  <thead className="bg-gray-50">
                    <tr className="text-left text-xs uppercase tracking-wider text-[#4B5563]">
                      <th className="px-6 py-3 font-headings">Fecha</th>
                      <th className="px-6 py-3 font-headings">Nombre</th>
                      <th className="px-6 py-3 font-headings">Email</th>
                      <th className="px-6 py-3 font-headings">WhatsApp</th>
                      <th className="px-6 py-3 font-headings">Provincia</th>
                      <th className="px-6 py-3 font-headings">Edad</th>
                      <th className="px-6 py-3 font-headings">Voluntario</th>
                      <th className="px-6 py-3 font-headings">Mensaje</th>
                    </tr>
                  </thead>
                  <tbody>
                    {rows.length === 0 && (
                      <tr><td colSpan={8} className="text-center py-10 text-[#9CA3AF]">Aún no hay registros.</td></tr>
                    )}
                    {rows.map((r) => (
                      <tr key={r.id} className="border-t border-gray-100 hover:bg-gray-50" data-testid={`admin-row-${r.id}`}>
                        <td className="px-6 py-3 text-[#4B5563] whitespace-nowrap">
                          {new Date(r.created_at).toLocaleDateString("es-AR")}
                        </td>
                        <td className="px-6 py-3 font-medium text-[#0A192F]">{r.nombre}</td>
                        <td className="px-6 py-3 text-[#4B5563]">{r.email}</td>
                        <td className="px-6 py-3 text-[#4B5563] whitespace-nowrap">
                          {r.whatsapp ? (
                            <a
                              href={`https://wa.me/${r.whatsapp.replace(/[^\d]/g, "")}`}
                              target="_blank"
                              rel="noopener noreferrer"
                              className="text-emerald-600 hover:text-emerald-700 underline-offset-2 hover:underline"
                            >
                              {r.whatsapp}
                            </a>
                          ) : "—"}
                        </td>
                        <td className="px-6 py-3 text-[#4B5563]">{r.provincia}</td>
                        <td className="px-6 py-3 text-[#4B5563]">{r.edad ?? "—"}</td>
                        <td className="px-6 py-3">
                          {r.quiere_voluntariar ? (
                            <span className="text-xs bg-[#F5B400] text-[#0A192F] px-2 py-1 font-headings uppercase tracking-wider">Sí</span>
                          ) : (
                            <span className="text-xs text-[#9CA3AF]">No</span>
                          )}
                        </td>
                        <td className="px-6 py-3 text-[#4B5563] max-w-md truncate">{r.mensaje || "—"}</td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            </div>
          </>
        )}

        {tab === "organizers" && (
          <div className="bg-white border border-gray-200" data-testid="admin-organizers-table">
            <div className="px-6 py-4 border-b border-gray-200 flex items-center justify-between">
              <h3 className="font-headings uppercase tracking-wide text-lg text-[#0A192F] inline-flex items-center gap-2">
                <Building2 size={18} className="text-[#0A2463]" />
                Referentes locales ({organizers.length})
              </h3>
            </div>
            <div className="overflow-x-auto">
              <table className="w-full text-sm">
                <thead className="bg-gray-50">
                  <tr className="text-left text-xs uppercase tracking-wider text-[#4B5563]">
                    <th className="px-6 py-3 font-headings">Fecha</th>
                    <th className="px-6 py-3 font-headings">Nombre</th>
                    <th className="px-6 py-3 font-headings">Email</th>
                    <th className="px-6 py-3 font-headings">WhatsApp</th>
                    <th className="px-6 py-3 font-headings">Ciudad</th>
                    <th className="px-6 py-3 font-headings">Provincia</th>
                    <th className="px-6 py-3 font-headings">Edad</th>
                    <th className="px-6 py-3 font-headings">Experiencia</th>
                    <th className="px-6 py-3 font-headings">Motivación</th>
                  </tr>
                </thead>
                <tbody>
                  {organizers.length === 0 && (
                    <tr><td colSpan={9} className="text-center py-10 text-[#9CA3AF]">Aún no hay referentes registrados.</td></tr>
                  )}
                  {organizers.map((o) => (
                    <tr key={o.id} className="border-t border-gray-100 hover:bg-gray-50" data-testid={`admin-org-${o.id}`}>
                      <td className="px-6 py-3 text-[#4B5563] whitespace-nowrap">
                        {new Date(o.created_at).toLocaleDateString("es-AR")}
                      </td>
                      <td className="px-6 py-3 font-medium text-[#0A192F]">{o.nombre}</td>
                      <td className="px-6 py-3 text-[#4B5563]">{o.email}</td>
                      <td className="px-6 py-3 text-[#4B5563] whitespace-nowrap">
                        <a
                          href={`https://wa.me/${o.whatsapp.replace(/[^\d]/g, "")}`}
                          target="_blank"
                          rel="noopener noreferrer"
                          className="text-emerald-600 hover:text-emerald-700 underline-offset-2 hover:underline"
                        >
                          {o.whatsapp}
                        </a>
                      </td>
                      <td className="px-6 py-3 text-[#0A192F] font-medium">{o.ciudad}</td>
                      <td className="px-6 py-3 text-[#4B5563]">{o.provincia}</td>
                      <td className="px-6 py-3 text-[#4B5563]">{o.edad ?? "—"}</td>
                      <td className="px-6 py-3 text-[#4B5563] max-w-xs truncate" title={o.experiencia}>{o.experiencia || "—"}</td>
                      <td className="px-6 py-3 text-[#4B5563] max-w-xs truncate" title={o.motivacion}>{o.motivacion || "—"}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </div>
        )}

        {tab === "donors" && (
          <DonorsManager
            donors={donors}
            stats={donorStats}
            adminKey={key}
            onChange={loadDonors}
            onDelete={deleteDonor}
          />
        )}

        {tab === "media" && (
          <>
            <div className="bg-[#0A192F] text-white p-5 sm:p-6 mb-8 flex flex-col sm:flex-row sm:items-center justify-between gap-4">
              <div>
                <div className="font-headings uppercase tracking-[0.2em] text-xs text-[#F5B400] mb-1">
                  Contenido inicial del movimiento
                </div>
                <p className="text-sm text-white/80 max-w-xl">
                  Cargá de una sola vez los <strong>6 flyers</strong>, <strong>3 stickers</strong> y el <strong>video oficial</strong>. Es idempotente: si ya existe algo, no lo duplica.
                </p>
              </div>
              <button
                onClick={seedContent}
                disabled={seeding}
                className="bg-[#F5B400] hover:bg-[#d99f00] disabled:opacity-60 text-[#0A192F] font-headings uppercase tracking-wide px-6 py-3 inline-flex items-center gap-2 whitespace-nowrap"
                data-testid="admin-seed-content"
              >
                {seeding ? <Loader2 className="animate-spin" size={16} /> : <Upload size={16} />}
                {seeding ? "Cargando…" : "Cargar contenido inicial"}
              </button>
            </div>
            <MediaManager media={media} adminKey={key} onChange={loadMedia} onDelete={deleteMedia} />
          </>
        )}

        {tab === "book" && (
          <div className="bg-white border border-gray-200 p-6 sm:p-8" data-testid="admin-book">
            <div className="flex items-center gap-3 mb-4">
              <BookOpen className="text-[#0A2463]" size={22} />
              <h3 className="font-headings uppercase tracking-wide text-lg text-[#0A192F]">
                Buscador del libro
              </h3>
            </div>
            <p className="text-sm text-[#4B5563] mb-6 max-w-2xl">
              El buscador del libro funciona contra una base de párrafos indexados.
              Después de cada deploy a producción, reindexá una vez para que la búsqueda funcione.
            </p>

            <div className="grid sm:grid-cols-3 gap-px bg-gray-200 border border-gray-200 mb-8">
              <div className="bg-white p-5">
                <div className="text-xs uppercase tracking-[0.2em] text-[#4B5563] font-headings">Párrafos indexados</div>
                <div className="font-headings text-3xl font-bold tabular-nums text-[#0A192F] mt-1">
                  {bookStats?.total_paragraphs ?? "—"}
                </div>
              </div>
              <div className="bg-white p-5">
                <div className="text-xs uppercase tracking-[0.2em] text-[#4B5563] font-headings">Páginas indexadas</div>
                <div className="font-headings text-3xl font-bold tabular-nums text-[#0A192F] mt-1">
                  {bookStats?.total_pages ?? "—"}
                </div>
              </div>
              <div className="bg-white p-5">
                <div className="text-xs uppercase tracking-[0.2em] text-[#4B5563] font-headings">Estado</div>
                <div className={`font-headings text-lg font-bold mt-1 ${
                  bookStats?.total_paragraphs > 0 ? "text-emerald-600" : "text-red-600"
                }`}>
                  {bookStats?.total_paragraphs > 0 ? "● Activo" : "● Vacío — reindexar"}
                </div>
              </div>
            </div>

            <button
              onClick={reindexBook}
              disabled={reindexing}
              className="bg-[#F5B400] hover:bg-[#d99f00] disabled:opacity-60 text-[#0A192F] font-headings uppercase tracking-wide px-8 py-3 inline-flex items-center gap-2"
              data-testid="admin-reindex-book"
            >
              {reindexing ? <Loader2 className="animate-spin" size={16} /> : <RefreshCcw size={16} />}
              {reindexing ? "Indexando…" : "Reindexar libro"}
            </button>
            <p className="text-xs text-[#9CA3AF] mt-3">
              Tarda 20-40 segundos. Descarga el PDF y reconstruye el índice de búsqueda.
            </p>

            {/* Audiolibro */}
            <AudiobookManager adminKey={key} />

            {/* Contador de descargas */}
            <div className="mt-12 pt-8 border-t border-gray-200">
              <div className="flex items-center gap-3 mb-4">
                <Download className="text-[#F5B400]" size={20} />
                <h3 className="font-headings uppercase tracking-wide text-lg text-[#0A192F]">
                  Descargas del libro
                </h3>
              </div>
              {!downloads ? (
                <p className="text-sm text-[#9CA3AF]">Cargando…</p>
              ) : (
                <>
                  <div className="grid sm:grid-cols-3 gap-px bg-gray-200 border border-gray-200 mb-6" data-testid="book-downloads-stats">
                    <Stat icon={<Download className="text-[#F5B400]" />} label="Total descargas" value={downloads.total} testid="dl-total" />
                    <Stat icon={<Clock className="text-[#0A2463]" />} label="Últimas 24 hs" value={downloads.last_24h} testid="dl-24h" />
                    <Stat icon={<Calendar className="text-[#0A2463]" />} label="Últimos 7 días" value={downloads.last_7d} testid="dl-7d" />
                  </div>

                  {downloads.por_origen?.length > 0 && (
                    <div className="bg-white border border-gray-200 p-5 mb-6">
                      <div className="text-[10px] uppercase tracking-[0.25em] text-[#4B5563] font-headings mb-3">
                        Por origen
                      </div>
                      <div className="space-y-2">
                        {downloads.por_origen.map((o) => {
                          const pct = downloads.total > 0 ? Math.round((o.total / downloads.total) * 100) : 0;
                          return (
                            <div key={o.source}>
                              <div className="flex justify-between text-sm mb-1">
                                <span className="text-[#0A192F]">{o.label}</span>
                                <span className="text-[#4B5563] tabular-nums">
                                  <strong className="text-[#0A2463]">{o.total}</strong>
                                  <span className="text-xs text-[#9CA3AF] ml-2">({pct}%)</span>
                                </span>
                              </div>
                              <div className="h-1.5 bg-gray-100 overflow-hidden">
                                <div className="h-full bg-[#F5B400]" style={{ width: `${pct}%` }} />
                              </div>
                            </div>
                          );
                        })}
                      </div>
                    </div>
                  )}

                  {downloads.daily?.length > 0 && (
                    <div className="bg-white border border-gray-200 p-5">
                      <div className="text-[10px] uppercase tracking-[0.25em] text-[#4B5563] font-headings mb-3">
                        Últimos 7 días
                      </div>
                      <div className="flex items-end gap-2 h-24">
                        {downloads.daily.map((d) => {
                          const max = Math.max(...downloads.daily.map((x) => x.downloads), 1);
                          const h = Math.max(4, Math.round((d.downloads / max) * 100));
                          return (
                            <div key={d.day} className="flex-1 flex flex-col items-center gap-1">
                              <div className="text-[10px] tabular-nums text-[#0A192F] font-headings">{d.downloads}</div>
                              <div className="w-full bg-[#F5B400]" style={{ height: `${h}%` }} title={`${d.downloads} descargas`} />
                              <div className="text-[9px] uppercase tracking-wider text-[#9CA3AF] font-headings">
                                {new Date(d.day + "T00:00:00").toLocaleDateString("es-AR", { weekday: "short", day: "2-digit" })}
                              </div>
                            </div>
                          );
                        })}
                      </div>
                    </div>
                  )}
                </>
              )}
            </div>
          </div>
        )}

        {tab === "press" && (
          <PressManager adminKey={key} />
        )}

        {tab === "red" && <RedAdmin adminKey={key} />}
      </main>
    </div>
  );
};

const Stat = ({ icon, label, value, testid }) => (
  <div className="bg-white p-6" data-testid={testid}>
    <div className="flex items-center gap-2 text-xs uppercase tracking-[0.2em] text-[#4B5563] font-headings">
      {icon} {label}
    </div>
    <div className="mt-2 font-headings text-4xl font-bold tabular-nums text-[#0A192F]">{value}</div>
  </div>
);

const DailyChart = ({ data }) => {
  const max = Math.max(...data.map((d) => d.visits), 1);
  const fmt = (day) => {
    const d = new Date(day + "T00:00:00");
    return d.toLocaleDateString("es-AR", { weekday: "short", day: "2-digit" });
  };
  return (
    <div className="flex items-end gap-2 h-32" data-testid="visits-chart">
      {data.map((d) => {
        const h = Math.max(4, Math.round((d.visits / max) * 100));
        return (
          <div key={d.day} className="flex-1 flex flex-col items-center gap-1 group">
            <div className="text-[10px] tabular-nums text-[#0A192F] font-headings">{d.visits}</div>
            <div
              className="w-full bg-[#0A2463] group-hover:bg-[#F5B400] transition-colors"
              style={{ height: `${h}%` }}
              title={`${d.visits} visitas · ${d.uniques} únicos`}
            />
            <div className="text-[9px] uppercase tracking-wider text-[#9CA3AF] font-headings">
              {fmt(d.day)}
            </div>
          </div>
        );
      })}
    </div>
  );
};

const MediaManager = ({ media, adminKey, onChange, onDelete }) => {
  const [mode, setMode] = useState("image"); // image | video | video-file
  const [titulo, setTitulo] = useState("");
  const [descripcion, setDescripcion] = useState("");
  const [url, setUrl] = useState("");
  const [file, setFile] = useState(null);
  const [busy, setBusy] = useState(false);

  const submit = async (e) => {
    e.preventDefault();
    if (!titulo.trim()) return toast.error("Título obligatorio");
    setBusy(true);
    try {
      if (mode === "image") {
        if (!file) {
          toast.error("Elegí una imagen");
          setBusy(false);
          return;
        }
        const fd = new FormData();
        fd.append("file", file);
        fd.append("titulo", titulo);
        fd.append("descripcion", descripcion);
        await axios.post(`${API}/admin/media/image`, fd, {
          headers: { "X-Admin-Key": adminKey },
        });
      } else if (mode === "video-file") {
        if (!file) {
          toast.error("Elegí un video");
          setBusy(false);
          return;
        }
        const fd = new FormData();
        fd.append("file", file);
        fd.append("titulo", titulo);
        fd.append("descripcion", descripcion);
        await axios.post(`${API}/admin/media/video-file`, fd, {
          headers: { "X-Admin-Key": adminKey },
          timeout: 60000,
        });
      } else {
        if (!url.trim()) {
          toast.error("Pegá el URL del video");
          setBusy(false);
          return;
        }
        await axios.post(
          `${API}/admin/media/video`,
          { titulo, descripcion, url },
          { headers: { "X-Admin-Key": adminKey } }
        );
      }
      toast.success("¡Cargado!");
      setTitulo("");
      setDescripcion("");
      setUrl("");
      setFile(null);
      await onChange();
    } catch (err) {
      toast.error(err.response?.data?.detail || "Error al subir");
    } finally {
      setBusy(false);
    }
  };

  return (
    <div data-testid="media-manager">
      {/* Uploader */}
      <div className="bg-white border border-gray-200 p-6 sm:p-8 mb-10">
        <h3 className="font-headings uppercase tracking-wide text-lg text-[#0A192F] mb-5">
          Subir contenido
        </h3>

        <div className="flex flex-wrap gap-2 mb-6">
          <button
            type="button"
            onClick={() => { setMode("image"); setFile(null); }}
            className={`px-4 py-2 font-headings uppercase tracking-wide text-sm border inline-flex items-center gap-2 ${
              mode === "image" ? "bg-[#0A2463] text-white border-[#0A2463]" : "border-gray-300 text-[#0A192F]"
            }`}
            data-testid="media-mode-image"
          >
            <ImgIcon size={16} /> Imagen / Flyer
          </button>
          <button
            type="button"
            onClick={() => { setMode("video-file"); setFile(null); }}
            className={`px-4 py-2 font-headings uppercase tracking-wide text-sm border inline-flex items-center gap-2 ${
              mode === "video-file" ? "bg-[#0A2463] text-white border-[#0A2463]" : "border-gray-300 text-[#0A192F]"
            }`}
            data-testid="media-mode-video-file"
          >
            <Film size={16} /> Video (archivo)
          </button>
          <button
            type="button"
            onClick={() => { setMode("video"); setFile(null); }}
            className={`px-4 py-2 font-headings uppercase tracking-wide text-sm border inline-flex items-center gap-2 ${
              mode === "video" ? "bg-[#0A2463] text-white border-[#0A2463]" : "border-gray-300 text-[#0A192F]"
            }`}
            data-testid="media-mode-video"
          >
            <Film size={16} /> Video (YouTube/Vimeo)
          </button>
        </div>

        <form onSubmit={submit} className="grid sm:grid-cols-2 gap-5">
          <div className="sm:col-span-2">
            <label className="block text-xs uppercase tracking-[0.2em] text-[#4B5563] mb-2 font-headings">Título *</label>
            <input
              value={titulo}
              onChange={(e) => setTitulo(e.target.value)}
              className="w-full border-b-2 border-gray-300 focus:border-[#0A2463] outline-none py-2 text-base"
              placeholder="Ej: Acto en Córdoba — 25 de mayo"
              data-testid="media-titulo"
            />
          </div>
          <div className="sm:col-span-2">
            <label className="block text-xs uppercase tracking-[0.2em] text-[#4B5563] mb-2 font-headings">Descripción</label>
            <input
              value={descripcion}
              onChange={(e) => setDescripcion(e.target.value)}
              className="w-full border-b-2 border-gray-300 focus:border-[#0A2463] outline-none py-2 text-base"
              placeholder="Opcional"
              data-testid="media-descripcion"
            />
          </div>

          {mode === "image" ? (
            <div className="sm:col-span-2">
              <label className="block text-xs uppercase tracking-[0.2em] text-[#4B5563] mb-2 font-headings">
                Imagen (JPG / PNG / WebP / GIF · máx 5 MB)
              </label>
              <input
                type="file"
                accept="image/jpeg,image/png,image/webp,image/gif"
                onChange={(e) => setFile(e.target.files?.[0] ?? null)}
                className="block w-full text-sm text-[#4B5563] file:mr-4 file:py-2 file:px-4 file:border-0 file:bg-[#0A2463] file:text-white file:font-headings file:uppercase file:tracking-wide file:cursor-pointer"
                data-testid="media-file"
              />
              {file && (
                <p className="mt-2 text-xs text-[#4B5563]">
                  {file.name} · {(file.size / 1024 / 1024).toFixed(2)} MB
                </p>
              )}
            </div>
          ) : mode === "video-file" ? (
            <div className="sm:col-span-2">
              <label className="block text-xs uppercase tracking-[0.2em] text-[#4B5563] mb-2 font-headings">
                Video (MP4 / WebM · máx 10 MB)
              </label>
              <input
                type="file"
                accept="video/mp4,video/webm,video/ogg,video/quicktime"
                onChange={(e) => setFile(e.target.files?.[0] ?? null)}
                className="block w-full text-sm text-[#4B5563] file:mr-4 file:py-2 file:px-4 file:border-0 file:bg-[#0A2463] file:text-white file:font-headings file:uppercase file:tracking-wide file:cursor-pointer"
                data-testid="media-video-file"
              />
              {file && (
                <p className="mt-2 text-xs text-[#4B5563]">
                  {file.name} · {(file.size / 1024 / 1024).toFixed(2)} MB
                </p>
              )}
              <p className="text-xs text-[#9CA3AF] mt-2">
                Para videos más grandes, subilo a YouTube/Vimeo y usá la URL.
              </p>
            </div>
          ) : (
            <div className="sm:col-span-2">
              <label className="block text-xs uppercase tracking-[0.2em] text-[#4B5563] mb-2 font-headings">URL del video *</label>
              <input
                value={url}
                onChange={(e) => setUrl(e.target.value)}
                className="w-full border-b-2 border-gray-300 focus:border-[#0A2463] outline-none py-2 text-base"
                placeholder="https://www.youtube.com/watch?v=..."
                data-testid="media-url"
              />
              <p className="text-xs text-[#9CA3AF] mt-1">Soporta YouTube, YouTube Shorts y Vimeo.</p>
            </div>
          )}

          <div className="sm:col-span-2 flex justify-end pt-2 border-t border-gray-200">
            <button
              type="submit"
              disabled={busy}
              className="bg-[#F5B400] hover:bg-[#d99f00] disabled:opacity-60 text-[#0A192F] font-headings uppercase tracking-wide px-8 py-3 inline-flex items-center gap-2"
              data-testid="media-submit"
            >
              {busy ? <Loader2 className="animate-spin" size={16} /> : <Upload size={16} />}
              Subir
            </button>
          </div>
        </form>
      </div>

      {/* Listado */}
      <div className="bg-white border border-gray-200" data-testid="media-list">
        <div className="px-6 py-4 border-b border-gray-200">
          <h3 className="font-headings uppercase tracking-wide text-lg text-[#0A192F]">
            Contenido cargado ({media.length})
          </h3>
        </div>
        {media.length === 0 ? (
          <div className="p-10 text-center text-[#9CA3AF]">Aún no hay contenido.</div>
        ) : (
          <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-0">
            {media.map((m) => (
              <div key={m.id} className="p-4 border-r border-b border-gray-200 last:border-r-0" data-testid={`admin-media-${m.id}`}>
                <div className="relative aspect-video bg-[#0A192F] mb-3 overflow-hidden">
                  {m.tipo === "image" ? (
                    <img src={m.data_url} alt={m.titulo} className="w-full h-full object-cover" />
                  ) : (m.video_url || "").startsWith("data:video/") || /\.(mp4|webm|ogg|mov|m4v)(\?|$)/i.test(m.video_url || "") ? (
                    <video src={m.video_url} controls className="w-full h-full bg-black" />
                  ) : (
                    <iframe src={m.video_url} className="w-full h-full" frameBorder="0" allowFullScreen title={m.titulo} />
                  )}
                  <span className="absolute top-2 left-2 text-[10px] uppercase tracking-wider bg-[#F5B400] text-[#0A192F] px-2 py-0.5 font-headings">
                    {m.tipo}
                  </span>
                </div>
                <div className="flex items-start justify-between gap-2">
                  <div className="flex-1 min-w-0">
                    <h4 className="font-headings text-sm uppercase tracking-tight text-[#0A192F] truncate">{m.titulo}</h4>
                    {m.descripcion && (
                      <p className="text-xs text-[#4B5563] truncate">{m.descripcion}</p>
                    )}
                    <p className="text-[10px] text-[#9CA3AF] mt-1">
                      {new Date(m.created_at).toLocaleDateString("es-AR")}
                    </p>
                  </div>
                  <button
                    onClick={() => onDelete(m.id)}
                    className="text-red-600 hover:text-red-700 p-1"
                    title="Eliminar"
                    data-testid={`media-delete-${m.id}`}
                  >
                    <Trash2 size={16} />
                  </button>
                </div>
              </div>
            ))}
          </div>
        )}
      </div>
    </div>
  );
};

export default Admin;

const METODOS = [
  { id: "mercadopago", label: "MercadoPago" },
  { id: "transferencia", label: "Transferencia" },
  { id: "efectivo", label: "Efectivo" },
  { id: "tarjeta", label: "Tarjeta" },
  { id: "cripto", label: "Cripto" },
  { id: "otro", label: "Otro" },
];

const DonorsManager = ({ donors, stats, adminKey, onChange, onDelete }) => {
  const initial = {
    nombre: "",
    email: "",
    whatsapp: "",
    monto: "",
    metodo: "transferencia",
    provincia: "",
    notas: "",
    fecha: "",
  };
  const [form, setForm] = useState(initial);
  const [busy, setBusy] = useState(false);

  const onChangeField = (e) => {
    const { name, value } = e.target;
    setForm((f) => ({ ...f, [name]: value }));
  };

  const submit = async (e) => {
    e.preventDefault();
    if (!form.nombre.trim() || !form.monto) {
      toast.error("Completá nombre y monto");
      return;
    }
    setBusy(true);
    try {
      await axios.post(
        `${API}/admin/donors`,
        { ...form, monto: Number(form.monto) },
        { headers: { "X-Admin-Key": adminKey } }
      );
      toast.success(`Aporte de ${form.nombre} registrado · $${Number(form.monto).toLocaleString("es-AR")}`);
      setForm(initial);
      await onChange();
    } catch (err) {
      toast.error(err.response?.data?.detail || "Error al registrar el aporte");
    } finally {
      setBusy(false);
    }
  };

  const fmtMoney = (n) => `$${Math.round(n || 0).toLocaleString("es-AR")}`;

  return (
    <div data-testid="donors-manager">
      {/* Stats */}
      {stats && (
        <div className="grid sm:grid-cols-3 gap-px bg-gray-200 border border-gray-200 mb-8" data-testid="donors-stats">
          <Stat icon={<HandCoins className="text-[#F5B400]" />} label="Total aportantes" value={stats.total} testid="donors-stat-total" />
          <Stat
            icon={<DollarSign className="text-emerald-600" />}
            label="Monto total recaudado"
            value={<span className="text-emerald-700">{fmtMoney(stats.monto_total)}</span>}
            testid="donors-stat-monto"
          />
          <Stat
            icon={<DollarSign className="text-[#0A2463]" />}
            label="Aporte promedio"
            value={fmtMoney(stats.monto_promedio)}
            testid="donors-stat-promedio"
          />
        </div>
      )}

      {/* Por método */}
      {stats?.por_metodo?.length > 0 && (
        <div className="bg-white border border-gray-200 p-6 mb-8">
          <h3 className="font-headings uppercase tracking-wide text-sm text-[#0A192F] mb-4">
            Aportes por método
          </h3>
          <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-3">
            {stats.por_metodo.map((m) => (
              <div key={m.metodo} className="flex justify-between items-center border-b border-gray-200 py-2">
                <span className="text-sm text-[#0A192F] capitalize">{m.metodo}</span>
                <span className="text-sm text-[#4B5563]">
                  <strong className="text-[#0A2463] tabular-nums">{m.cantidad}</strong>
                  <span className="mx-2 text-gray-300">·</span>
                  <span className="text-emerald-700 font-headings tabular-nums">{fmtMoney(m.monto)}</span>
                </span>
              </div>
            ))}
          </div>
        </div>
      )}

      {/* Formulario alta manual */}
      <div className="bg-white border border-gray-200 p-6 sm:p-8 mb-10">
        <div className="flex items-center gap-3 mb-5">
          <Plus size={18} className="text-[#F5B400]" />
          <h3 className="font-headings uppercase tracking-wide text-lg text-[#0A192F]">
            Registrar aporte manualmente
          </h3>
        </div>
        <p className="text-xs text-[#9CA3AF] mb-6">
          Mientras MercadoPago no esté integrado, cargá acá los aportes que recibas por transferencia, efectivo o tarjeta.
        </p>

        <form onSubmit={submit} className="grid sm:grid-cols-2 gap-5" noValidate>
          <FieldA label="Nombre *">
            <input
              name="nombre"
              value={form.nombre}
              onChange={onChangeField}
              className="input-line"
              placeholder="Nombre y apellido"
              data-testid="donor-input-nombre"
            />
          </FieldA>
          <FieldA label="Monto (ARS) *">
            <input
              type="number"
              min="0"
              step="100"
              name="monto"
              value={form.monto}
              onChange={onChangeField}
              className="input-line"
              placeholder="5000"
              data-testid="donor-input-monto"
            />
          </FieldA>
          <FieldA label="Método">
            <select
              name="metodo"
              value={form.metodo}
              onChange={onChangeField}
              className="input-line appearance-none cursor-pointer bg-transparent"
              data-testid="donor-input-metodo"
            >
              {METODOS.map((m) => (
                <option key={m.id} value={m.id}>{m.label}</option>
              ))}
            </select>
          </FieldA>
          <FieldA label="Fecha del aporte (opcional)">
            <input
              type="date"
              name="fecha"
              value={form.fecha}
              onChange={onChangeField}
              className="input-line"
              data-testid="donor-input-fecha"
            />
          </FieldA>
          <FieldA label="Email (opcional)">
            <input
              type="email"
              name="email"
              value={form.email}
              onChange={onChangeField}
              className="input-line"
              placeholder="correo@ejemplo.com"
              data-testid="donor-input-email"
            />
          </FieldA>
          <FieldA label="WhatsApp (opcional)">
            <input
              type="tel"
              name="whatsapp"
              value={form.whatsapp}
              onChange={onChangeField}
              className="input-line"
              placeholder="+54 11 1234 5678"
              data-testid="donor-input-whatsapp"
            />
          </FieldA>
          <FieldA label="Provincia (opcional)">
            <select
              name="provincia"
              value={form.provincia}
              onChange={onChangeField}
              className="input-line appearance-none cursor-pointer bg-transparent"
              data-testid="donor-input-provincia"
            >
              <option value="">— Sin especificar —</option>
              {PROVINCIAS_AR.map((p) => (
                <option key={p} value={p}>{p}</option>
              ))}
            </select>
          </FieldA>
          <FieldA label="Notas (opcional)" className="sm:col-span-2">
            <textarea
              name="notas"
              value={form.notas}
              onChange={onChangeField}
              rows={2}
              className="input-line resize-none"
              placeholder="Comentarios internos: # de operación, banco, observaciones."
              data-testid="donor-input-notas"
            />
          </FieldA>

          <div className="sm:col-span-2 flex justify-end pt-4 border-t border-gray-200">
            <button
              type="submit"
              disabled={busy}
              className="bg-[#F5B400] hover:bg-[#d99f00] disabled:opacity-60 text-[#0A192F] font-headings uppercase tracking-wide px-8 py-3 inline-flex items-center gap-2"
              data-testid="donor-submit"
            >
              {busy ? <Loader2 className="animate-spin" size={16} /> : <Plus size={16} />}
              {busy ? "Registrando…" : "Registrar aporte"}
            </button>
          </div>
        </form>
      </div>

      {/* Listado */}
      <div className="bg-white border border-gray-200" data-testid="donors-list">
        <div className="px-6 py-4 border-b border-gray-200">
          <h3 className="font-headings uppercase tracking-wide text-lg text-[#0A192F]">
            Aportes registrados ({donors.length})
          </h3>
        </div>
        <div className="overflow-x-auto">
          <table className="w-full text-sm">
            <thead className="bg-gray-50">
              <tr className="text-left text-xs uppercase tracking-wider text-[#4B5563]">
                <th className="px-6 py-3 font-headings">Fecha</th>
                <th className="px-6 py-3 font-headings">Nombre</th>
                <th className="px-6 py-3 font-headings text-right">Monto</th>
                <th className="px-6 py-3 font-headings">Método</th>
                <th className="px-6 py-3 font-headings">Provincia</th>
                <th className="px-6 py-3 font-headings">Email / WhatsApp</th>
                <th className="px-6 py-3 font-headings">Notas</th>
                <th className="px-6 py-3 font-headings" />
              </tr>
            </thead>
            <tbody>
              {donors.length === 0 && (
                <tr><td colSpan={8} className="text-center py-10 text-[#9CA3AF]">Aún no hay aportes registrados.</td></tr>
              )}
              {donors.map((d) => (
                <tr key={d.id} className="border-t border-gray-100 hover:bg-gray-50" data-testid={`donor-row-${d.id}`}>
                  <td className="px-6 py-3 text-[#4B5563] whitespace-nowrap">
                    {d.fecha || new Date(d.created_at).toLocaleDateString("es-AR")}
                  </td>
                  <td className="px-6 py-3 font-medium text-[#0A192F]">{d.nombre}</td>
                  <td className="px-6 py-3 text-emerald-700 font-headings tabular-nums text-right whitespace-nowrap">
                    {fmtMoney(d.monto)}
                  </td>
                  <td className="px-6 py-3 text-[#4B5563] capitalize">{d.metodo}</td>
                  <td className="px-6 py-3 text-[#4B5563]">{d.provincia || "—"}</td>
                  <td className="px-6 py-3 text-[#4B5563]">
                    <div className="text-xs">{d.email || "—"}</div>
                    {d.whatsapp && (
                      <a
                        href={`https://wa.me/${d.whatsapp.replace(/[^\d]/g, "")}`}
                        target="_blank"
                        rel="noopener noreferrer"
                        className="text-xs text-emerald-600 hover:text-emerald-700 hover:underline"
                      >
                        {d.whatsapp}
                      </a>
                    )}
                  </td>
                  <td className="px-6 py-3 text-[#4B5563] max-w-xs truncate" title={d.notas}>{d.notas || "—"}</td>
                  <td className="px-6 py-3 text-right">
                    <button
                      onClick={() => onDelete(d.id)}
                      className="text-red-600 hover:text-red-700 p-1"
                      title="Eliminar"
                      data-testid={`donor-delete-${d.id}`}
                    >
                      <Trash2 size={16} />
                    </button>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </div>
    </div>
  );
};

const FieldA = ({ label, children, className = "" }) => (
  <div className={className}>
    <label className="block text-xs uppercase tracking-[0.2em] text-[#4B5563] mb-2 font-headings">
      {label}
    </label>
    {children}
  </div>
);

/* ============== Audiolibro ============== */
const VOICES = [
  { id: "onyx", label: "Onyx · grave (recomendada)" },
  { id: "alloy", label: "Alloy · neutral" },
  { id: "echo", label: "Echo · calma" },
  { id: "fable", label: "Fable · narrador" },
  { id: "nova", label: "Nova · cálida" },
  { id: "shimmer", label: "Shimmer · brillante" },
  { id: "ash", label: "Ash · clara" },
  { id: "coral", label: "Coral · amistosa" },
  { id: "sage", label: "Sage · pausada" },
];
const MODELS = [
  { id: "tts-1", label: "TTS-1 · rápido (más barato)" },
  { id: "tts-1-hd", label: "TTS-1 HD · alta definición" },
];

const AudiobookManager = ({ adminKey }) => {
  const [list, setList] = useState([]);
  const [jobs, setJobs] = useState({}); // {capitulo: {status, chunks_done, chunks, error}}
  const [voice, setVoice] = useState("onyx");
  const [model, setModel] = useState("tts-1");
  const [speed, setSpeed] = useState(1.0);
  const settingsLoadedRef = useRef(false);

  const load = async () => {
    try {
      const [a, j, s] = await Promise.all([
        axios.get(`${API}/book/audio`),
        axios.get(`${API}/admin/book/audio/jobs`, { headers: { "X-Admin-Key": adminKey } }),
        axios.get(`${API}/book/audio/settings`),
      ]);
      setList(a.data);
      setJobs(Object.fromEntries(j.data.map((x) => [x.capitulo, x])));
      if (s.data) {
        if (s.data.voice) setVoice(s.data.voice);
        if (s.data.model) setModel(s.data.model);
        if (typeof s.data.speed === "number") setSpeed(s.data.speed);
      }
      settingsLoadedRef.current = true;
    } catch (e) { /* */ }
  };
  useEffect(() => { load(); }, []);

  // Auto-guarda settings al cambiar voz/modelo/velocidad (debounced)
  useEffect(() => {
    if (!settingsLoadedRef.current) return;
    const t = setTimeout(() => {
      axios.post(
        `${API}/admin/book/audio/settings`,
        { voice, model, speed },
        { headers: { "X-Admin-Key": adminKey } }
      ).catch(() => {});
    }, 600);
    return () => clearTimeout(t);
  }, [voice, model, speed, adminKey]);

  // Polling: refresca cada 5s si hay algún job en "generating"
  useEffect(() => {
    const hasActive = Object.values(jobs).some((j) => j.status === "generating");
    if (!hasActive) return;
    const t = setInterval(load, 5000);
    return () => clearInterval(t);
  }, [jobs]);

  const byCap = useMemo(() => Object.fromEntries(list.map((r) => [r.capitulo, r])), [list]);

  const generar = async (cap) => {
    try {
      await axios.post(
        `${API}/admin/book/audio/generate`,
        { capitulo: cap, voice, model, speed },
        { headers: { "X-Admin-Key": adminKey }, timeout: 20000 }
      );
      toast.success(`Cap. ${cap} en cola · se genera en segundo plano (puede tardar varios minutos)`);
      await load();
    } catch (e) {
      toast.error(e.response?.data?.detail || "Falló al encolar la generación");
    }
  };

  const borrar = async (cap) => {
    if (!window.confirm(`¿Eliminar audio del Cap. ${cap}?`)) return;
    try {
      await axios.delete(`${API}/admin/book/audio/${cap}`, { headers: { "X-Admin-Key": adminKey } });
      toast.success("Eliminado");
      await load();
    } catch (e) { toast.error("No se pudo eliminar"); }
  };

  return (
    <div className="mt-12 pt-8 border-t border-gray-200" data-testid="admin-audiobook">
      <div className="flex items-center gap-3 mb-4">
        <span className="text-[#F5B400] text-xl">🔊</span>
        <h3 className="font-headings uppercase tracking-wide text-lg text-[#0A192F]">
          Audiolibro · OpenAI TTS
        </h3>
      </div>
      <p className="text-sm text-[#4B5563] mb-6 max-w-2xl">
        Generá el audio de cada capítulo del libro. Usa tu API key de OpenAI. Costo aproximado:
        <strong> ~USD 0,15 por capítulo</strong> en TTS-1 estándar.
      </p>

      <div className="grid sm:grid-cols-3 gap-5 mb-6 p-5 bg-gray-50 border border-gray-200">
        <FieldA label="Voz">
          <select value={voice} onChange={(e) => setVoice(e.target.value)} className="input-line appearance-none cursor-pointer bg-transparent" data-testid="audio-voice">
            {VOICES.map((v) => <option key={v.id} value={v.id}>{v.label}</option>)}
          </select>
        </FieldA>
        <FieldA label="Modelo">
          <select value={model} onChange={(e) => setModel(e.target.value)} className="input-line appearance-none cursor-pointer bg-transparent" data-testid="audio-model">
            {MODELS.map((m) => <option key={m.id} value={m.id}>{m.label}</option>)}
          </select>
        </FieldA>
        <FieldA label={`Velocidad: ${speed.toFixed(2)}x`}>
          <input type="range" min="0.75" max="1.5" step="0.05" value={speed}
            onChange={(e) => setSpeed(parseFloat(e.target.value))}
            className="w-full accent-[#F5B400]" data-testid="audio-speed" />
        </FieldA>
      </div>
      <p className="text-[10px] uppercase tracking-[0.2em] font-headings text-[#9CA3AF] -mt-3 mb-6">
        Configuración sincronizada con el reproductor público. La velocidad se aplica en tiempo real; la voz se aplica a nuevos capítulos generados.
      </p>

      <div className="grid grid-cols-3 sm:grid-cols-5 lg:grid-cols-6 gap-3" data-testid="audiobook-grid">
        {Array.from({ length: 30 }, (_, i) => i + 1).map((cap) => {
          const has = !!byCap[cap];
          const job = jobs[cap];
          const isGen = job?.status === "generating";
          const hasError = job?.status === "error";
          return (
            <div key={cap}
              className={`border p-3 ${has ? "border-emerald-300 bg-emerald-50" : isGen ? "border-[#F5B400] bg-yellow-50" : hasError ? "border-red-300 bg-red-50" : "border-gray-200 bg-white"}`}
              data-testid={`audio-card-${cap}`}
            >
              <div className="flex items-center justify-between mb-2">
                <span className="font-headings uppercase text-xs text-[#0A192F]">Cap. {cap}</span>
                {has && (
                  <button onClick={() => borrar(cap)} className="text-red-500 hover:text-red-700" title="Eliminar">
                    <Trash2 size={12} />
                  </button>
                )}
              </div>
              {has ? (
                <>
                  <audio controls src={byCap[cap].file} className="w-full h-8" preload="none" />
                  <div className="text-[10px] text-[#9CA3AF] uppercase tracking-wider font-headings mt-1">
                    {byCap[cap].voice} · {(byCap[cap].bytes / 1024 / 1024).toFixed(1)} MB
                  </div>
                </>
              ) : isGen ? (
                <div className="text-center py-2">
                  <Loader2 className="animate-spin mx-auto text-[#F5B400]" size={20} />
                  <div className="text-[10px] text-[#0A192F] font-headings uppercase tracking-wider mt-1">
                    Generando… {job.chunks_done ?? 0}/{job.chunks ?? "?"}
                  </div>
                </div>
              ) : (
                <button
                  onClick={() => generar(cap)}
                  className="w-full text-xs bg-[#F5B400] hover:bg-[#d99f00] text-[#0A192F] font-headings uppercase tracking-wide py-2 inline-flex items-center justify-center gap-1.5"
                  data-testid={`audio-gen-${cap}`}
                  title={hasError ? `Error previo: ${job.error}. Click para reintentar.` : ""}
                >
                  {hasError ? "Reintentar" : "Generar"}
                </button>
              )}
            </div>
          );
        })}
      </div>
      <p className="text-xs text-[#9CA3AF] mt-4">
        Cada capítulo tarda 30 segundos a varios minutos en generarse en segundo plano.
        Podés cerrar esta pestaña tranquilo: la generación sigue corriendo. Refresca y vas a ver
        el reproductor verde cuando esté listo.
      </p>
    </div>
  );
};
